import { ObjectCompositeFilterModel } from '@base/modules/rest/object/model/object-composite-filter.model';
import { ObjectCompositeFilterItemModel } from '@shared/components/filter/model/object-composite-filter-item.model';
import { FilterOperationEnum } from '@shared/components/filter/enums/filter-operation.enum';
import { ExpressionOperatorEnum } from '@base/modules/rest/object/enums/expression-operator.enum';
import { createObjectField, CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';
import { CustomFieldTypesEnum } from '@base/modules/rest/custom-field/enums/custom-field-types.enum';
import * as moment from 'moment-timezone';
import { clearTime, MAT_DATE_FORMAT } from '@base/utils/date.util';
import { ObjectDefinitionModel } from '@base/modules/rest/bbs-object/model/object-definition.model';
import { capitalizePath, isPath, pathGetLast, pathSkipLast, splitFieldPaths } from '@base/utils/common.util';
import { LookupFieldFilterItemModel } from '@modules/master-data/model/lookup-field-filter-item.model';
import { LookupFieldFilterTypeEnum } from '@modules/master-data/model/enums/lookup-field-filter-type.enum';
import { findFieldForFilter } from '@shared/components/filter/pipes/applied-filters-chips-display.pipe';

export const DEFAULT_OPERATION_FILTER = FilterOperationEnum.EQUALS;

export function prepareCompositeFilter(compositeFilter: ObjectCompositeFilterModel, filter: string, fields: CustomFieldModel[]): ObjectCompositeFilterModel {
  if (!compositeFilter && !filter) {
    return undefined;
  }
  const filterSearch = prepareSearchFilter(filter, fields);
  const previousCompositeFilter: ObjectCompositeFilterModel = {
    filters: compositeFilter?.filters?.map(f => {
      if (f?.value?.id) {
        f = {
          ...f,
          value: f?.value?.id,
        };
      }
      return f;
    }) || [],
    expression: compositeFilter?.expression || '',
  };
  const expression = mergeCompositeFiltersExpression(filterSearch, previousCompositeFilter);
  return {
    id: compositeFilter?.id,
    filters: [...filterSearch.filters, ...previousCompositeFilter.filters],
    showChips: compositeFilter?.showChips,
    expression: expression,
  };
}

export function mergeCompositeFilters(filter1: ObjectCompositeFilterModel, filter2: ObjectCompositeFilterModel): ObjectCompositeFilterModel {
  if (!filter1 && !filter2) {
    return null;
  }
  if (!filter1) {
    return filter2;
  }
  if (!filter2) {
    return filter1;
  }

  const filters = [
    ...filter1.filters,
    ...filter2.filters,
  ];

  const expression = mergeCompositeFiltersExpression(filter1, filter2);

  return {
    filters,
    expression,
  };
}

export function mergeCompositeFiltersExpression(filter1: ObjectCompositeFilterModel, filter2: ObjectCompositeFilterModel): string {
  let expression = '';
  if (filter1?.expression) {
    expression += filter1.expression;
  }
  if (filter2?.expression) {
    if (expression) {
      expression = `(${expression}) AND (${filter2.expression})`;
    } else {
      expression += filter2.expression;
    }
  }
  return expression;
}


export function prepareSearchFilter(filter: string, fields: CustomFieldModel[]): ObjectCompositeFilterModel {
  if (!filter || filter.length === 0) {
    return {
      filters: [],
      expression: '',
    };
  }
  const compositeFilter = {filters: [], expression: ''};
  fields
    .filter(field => field.visible && field.filterable)
    .filter(field => !(field.type === CustomFieldTypesEnum.LOOKUP && !field.system))
    .forEach((field, i) => {
      if (field.type === CustomFieldTypesEnum.LOOKUP) {
        const lookupFilters = [];
        let filterIndex = 1;

        // Process mainField - support multiple paths separated by spaces
        if (field?.lookupObject?.mainField) {
          const mainFieldPaths = splitFieldPaths(field.lookupObject.mainField);
          mainFieldPaths.forEach(path => {
            lookupFilters.push(createFieldFilter(`item_lookup_${i}_${filterIndex++}`, field.path, path, filter));
          });
        }

        // Process secondField - support multiple paths separated by spaces
        if (field?.lookupObject?.secondField) {
          const secondFieldPaths = splitFieldPaths(field.lookupObject.secondField);
          secondFieldPaths.forEach(path => {
            lookupFilters.push(createFieldFilter(`item_lookup_${i}_${filterIndex++}`, field.path, path, filter));
          });
        }

        compositeFilter.filters = [...compositeFilter.filters, ...lookupFilters];
      } else if (field.type === CustomFieldTypesEnum.DATE) {
        const dateValue = moment(filter, MAT_DATE_FORMAT, true);
        const isDateValid = dateValue.isValid();
        if (isDateValid) {
          const filterItem: ObjectCompositeFilterItemModel = createFieldFilterItem(`item_${i}`, field, clearTime(dateValue), FilterOperationEnum.EQUALS);
          compositeFilter.filters = [...compositeFilter.filters, filterItem];
        }
      } else if (field.type === CustomFieldTypesEnum.NUMBER || field.type === CustomFieldTypesEnum.DECIMAL_NUMBER) {
        if (!isNumberString(filter)) {
          console.warn(`Filter value '${filter}' is not a number!`);
        } else {
          const filterItem: ObjectCompositeFilterItemModel = createFieldFilterItem(`item_${i}`, field, filter, FilterOperationEnum.EQUALS);
          compositeFilter.filters = [...compositeFilter.filters, filterItem];
        }
      } else if (field.type === CustomFieldTypesEnum.SWITCHER) {
        console.warn(`Filter value '${filter}' is not handled for SWITCHER field type!`);
      } else if (field.type === CustomFieldTypesEnum.SELECT) {
        console.warn(`Filter value '${filter}' is not handled for SELECT field type!`);
      } else {
        const filterItem: ObjectCompositeFilterItemModel = createFieldFilterItem(`item_${i}`, field, filter);
        compositeFilter.filters = [...compositeFilter.filters, filterItem];
      }
    });
  generateCompositeFilterExpression(compositeFilter, ExpressionOperatorEnum.OR);
  return compositeFilter;
}

function isNumberString(value) {
  return typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value));
}

export function createFieldFilter(name: string, path: string, fieldName: string, filter: any, operator?: FilterOperationEnum): ObjectCompositeFilterItemModel {
  return {
    name,
    beanName: path,
    fieldName: fieldName,
    operation: operator ?? FilterOperationEnum.CONTAINS,
    value: filter,
  };
}

export function createFieldFilterItem(name: string,
                                      field: CustomFieldModel,
                                      filter: any,
                                      operator?: FilterOperationEnum): ObjectCompositeFilterItemModel {
  let fieldName: string;
  if (field.type === CustomFieldTypesEnum.LOOKUP) {
    fieldName = 'id';
  } else {
    // Changed from 'pathGetLast(field.dbName);' to 'field.dbName' because of ColumnInfo.relative fields
    fieldName = pathGetLast(field.dbName);
  }
  const _operator = operator ?? (fieldName === 'id' ? FilterOperationEnum.EQUALS : FilterOperationEnum.CONTAINS);
  return {
    name: name,
    beanName: field.path,
    fieldName: fieldName,
    operation: _operator,
    value: filter,
  };
}

export function generateCompositeFilterExpression(compositeFilter: ObjectCompositeFilterModel,
                                                  expressionOperator: ExpressionOperatorEnum): void {
  compositeFilter.expression = '';
  compositeFilter.filters
    .forEach((f: ObjectCompositeFilterItemModel, i: number, array: ObjectCompositeFilterItemModel[]) => {
        const snippet = i !== array.length - 1 ? `${f.name} ${expressionOperator} ` : `${f.name}`;
        f.displayOrder = i + 1;
        compositeFilter.expression = compositeFilter.expression.concat(snippet);
      }
    );
}

export function prepareLookupFilter(object: ObjectDefinitionModel, searchFilter: string): ObjectCompositeFilterModel {
  const fields: CustomFieldModel[] = [];

  // Process mainField - support multiple paths separated by spaces
  if (!!object.mainField) {
    const mainFieldPaths = splitFieldPaths(object.mainField);
    mainFieldPaths.forEach(path => {
      const field = findField(object, path);
      if (!!field) {
        fields.push(field);
      } else {
        fields.push(createObjectField(object.name, path));
      }
    });
  }

  // Process secondField - support multiple paths separated by spaces
  if (!!object.secondField) {
    const secondFieldPaths = splitFieldPaths(object.secondField);
    secondFieldPaths.forEach(path => {
      const field = findField(object, path);
      if (!!field) {
        fields.push(field);
      } else {
        fields.push(createObjectField(object.name, path));
      }
    });
  }

  return prepareSearchFilter(searchFilter, fields);
}

export function prepareFilter(object: ObjectDefinitionModel, filter: ObjectCompositeFilterModel, searchFilter: string): ObjectCompositeFilterModel {
  const lookupFilter = prepareLookupFilter(object, searchFilter);
  if (filter) {
    lookupFilter.filters.push(...filter.filters);
    if (filter?.expression) {
      if (lookupFilter.expression?.length) {
        lookupFilter.expression = `(${lookupFilter.expression}) AND `;
      }
      lookupFilter.expression += `(${filter.expression})`;
    }
  }
  return lookupFilter;
}

function findField(object: ObjectDefinitionModel, dbName: string): CustomFieldModel {
  return object.fields?.find(f => f.dbName === dbName);
}

/**
 * Converts lookup field filter items to object composite filter items
 * @param field The lookup field definition
 * @param fieldFilters Array of lookup field filter items
 * @param getData Function to get data values for parent field filters
 * @returns Array of object composite filter items
 */
export function transformLookupFieldFiltersToCompositionFilters(
  field: CustomFieldModel,
  fieldFilters: LookupFieldFilterItemModel[],
  getData: (fieldName: string) => any = () => null,
  options?: { mapOnlyParentParams?: boolean }
): ObjectCompositeFilterItemModel[] {
  return fieldFilters
    .map(filter => {
      if (filter.type === LookupFieldFilterTypeEnum.HQL) {
        const existingParameters = filter.parameters;
        const mappedParameters = mapHqlParameters(filter.hqlExpression, getData, options);
        const parameters = existingParameters || mappedParameters
          ? {...(existingParameters || {}), ...(mappedParameters || {})}
          : {};
        return {
          name: filter.name,
          hqlExpression: filter.hqlExpression,
          parameters,
        } as ObjectCompositeFilterItemModel;
      }

      let beanName = field.lookupObject?.name;
      let fieldName = filter.fieldPath;
      if (isPath(filter.fieldPath)) {
        beanName += '.' + capitalizePath(pathSkipLast(filter.fieldPath));
        fieldName = pathGetLast(filter.fieldPath);
      }

      if (filter.type === LookupFieldFilterTypeEnum.RELATED_OBJECT) {
        return createFieldFilter(filter.name, beanName, fieldName, filter.value, filter.operation);
      } else {
        const operation = filter.operation;
        if (operation === FilterOperationEnum.HAS_VALUE || operation === FilterOperationEnum.DOES_NOT_HAVE_VALUE) {
          return createFieldFilter(filter.name, beanName, fieldName, '', filter.operation);
        } else {
          const value = getData(filter.parentFieldName);
          if (value === 0 || !!value) {
            return createFieldFilter(filter.name, beanName, fieldName, value, filter.operation);
          } else {
            console.error('Vrednost izabranog polja sa matičnog objekta mora imati vrednost!');
            return null;
          }
        }
      }
    });
}

export function mapHqlParameters(
  hqlExpression: string,
  getData: (fieldName: string) => any,
  options?: { mapOnlyParentParams?: boolean }
): Record<string, any> | undefined {
  if (!hqlExpression) {
    return undefined;
  }

  const onlyParent = options?.mapOnlyParentParams;
  const parentPrefix = '#PARENT.';
  const fieldPrefix = '#FIELD.';

  const escapedParentPrefix = parentPrefix.replace(/[-/\\^$*+?.()|[\\]{}]/g, '\\$&');
  const escapedFieldPrefix = fieldPrefix.replace(/[-/\\^$*+?.()|[\\]{}]/g, '\\$&');

  const parentParamsRegex = new RegExp(`:${escapedParentPrefix}([a-zA-Z0-9_.]+)`, 'g');
  const fieldParamsRegex = new RegExp(`:${escapedFieldPrefix}([a-zA-Z0-9_.]+)`, 'g');

  const parameters: Record<string, any> = {};
  if (onlyParent) {
    collectParameters(parentParamsRegex, parentPrefix, parameters, hqlExpression, getData);
  } else {
    collectParameters(fieldParamsRegex, fieldPrefix, parameters, hqlExpression, getData);
  }
  return Object.keys(parameters).length ? parameters : undefined;
}

function collectParameters(
  regex: RegExp,
  prefix: string,
  parameters: Record<string, any>,
  hqlExpression: string,
  getData: (fieldName: string) => any
): void {
  regex.lastIndex = 0;
  let match: RegExpExecArray;
  while ((match = regex.exec(hqlExpression))) {
    const valueKey = match[1];
    parameters[`${prefix}${valueKey}`] = getData(valueKey);
  }
}

export function setFilterValue(field: CustomFieldModel, value: any): any {
  if (value === null || value === undefined) {
    if (field.type === CustomFieldTypesEnum.TEXT ||
      field.type === CustomFieldTypesEnum.TEXTAREA ||
      field.type === CustomFieldTypesEnum.EMAIL ||
      field.type === CustomFieldTypesEnum.URL) {
      return '';
    }
  }
  return value;
}

export function compositeFilterNullsFix(objectDefinition: ObjectDefinitionModel, compositeFilter: ObjectCompositeFilterModel): ObjectCompositeFilterModel {
  if (!compositeFilter) {
    return compositeFilter;
  }
  if (!compositeFilter?.filters) {
    return compositeFilter;
  }
  for (const filter of compositeFilter.filters) {
    const field = findFieldForFilter(objectDefinition.filters, filter);
    if (!field) {
      continue;
    }
    filter.value = setFilterValue(field, filter.value);
  }
  return compositeFilter;
}
